/* eslint-disable no-nested-ternary */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { uuid } from '@carvana/purchase-utils';

const Disabled = css`
  cursor: not-allowed;
`;

const Container = styled.div`
  height: 60px;
  border-radius: 3px;
  background: ${props =>
    props.disabled
      ? props.cosigner
        ? '#ffffff'
        : props.theme.carvana.gray.background
      : '#ffffff'};
  border: solid 1px
    ${props =>
      props.error
        ? '#e03131'
        : props.isFocused
        ? props.theme.carvana.blue.primary
        : props.theme.carvana.gray.light};
  box-sizing: border-box;
  padding: 0.75rem 1rem 0 1rem;
  position: relative;
  width: ${props => props.width};

  &:hover,
  &:focus {
    border: solid 1px
      ${props => (props.error ? '#e03131' : props.theme.carvana.blue.primary)};

    outline: none;
  }

  ${props => props.disabled && Disabled}
`;

const SelectInputWrapper = styled.div`
  position: relative;
  display: inline;
  color: ${props => props.theme.carvana.gray.dark};

  &:after {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    pointer-events: none;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    top: 0.3em;
    right: 0.25em;
    border-top: 8px solid ${props => props.theme.carvana.blue.primary};
    opacity: 1;

    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
      opacity: 0;
    }
  }
`;

const SelectInput = styled.select`
  border: none;
  font-size: 14px;
  padding-top: 12px;
  padding-left: 5px;
  padding-bottom: 2px;
  margin-left: -5px;
  color: ${props =>
    props.error ? '#e03131' : '#183558'};
  outline: 0;
  width: 100%;
  background-color: ${props => props.transparent && 'transparent'};
  ${props => props.cosigner && 'background-color: #ffffff'};
  height: 2.5rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  appearance: none;
  position: relative;
  border-radius: 0;
  ${props => props.bold && 'font-weight: 700;'};

  &:-ms-expand {
    display: none;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
  }

  &:focus {
    border-bottom: solid 1px
      ${props => (props.error ? '#e03131' : props.theme.carvana.blue)};

    outline: none;
  }

  ${props => props.disabled && Disabled}
`;

const Label = styled.label`
  font-size: 10px;
  font-weight: bold;
  position: absolute;
  color: ${props => props.theme.carvana.gray.dark};
  color: ${props => props.isFocused && props.theme.carvana.blue.primary};
  color: ${props => props.error && '#e03131'};
  text-transform: uppercase;

  ${props => props.disabled && Disabled}
`;

const ErrorLabel = styled.label`
  font-size: 12px;
  margin-top: 5px;
  color: ${props => props.theme.carvana.gray.dark};
  color: ${props => props.isFocused && props.theme.carvana.blue.primary};
  color: ${props => props.error && '#e03131'};
  display: ${props => (props.hideErrorMessage ? 'none' : 'block')};

  ${props => props.disabled && Disabled}
`;

const InputTip = styled.div`
  color: ${props => props.theme.carvana.grayFour};
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  margin-top: 8px;
`;

const InputTipNote = styled.span`
  color: ${props => props.theme.carvana.orange};
`;

const SameStateNote = styled.div`
  color: #757989;
  font-size: 12px;
  font-weight: 390;
  margin-top: 4px;
  line-height: 16px;
`;

class FloatSelect extends PureComponent {
  inputRef = React.createRef();

  static propTypes = {
    label: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.element
    ]).isRequired,
    onChange: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func,
    onTouched: PropTypes.func,
    onBlur: PropTypes.func,
    touched: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.string,
    'data-cv-test-override': PropTypes.string,
    hideLabel: PropTypes.bool,
    bold: PropTypes.bool,
    canContainEmptyDefaultValue: PropTypes.bool,
    disabled: PropTypes.bool,
    cosigner: PropTypes.bool,
    showSameStateText: PropTypes.bool,
    hideErrorMessage: PropTypes.bool,
    scrollTag: PropTypes.string
  };

  static defaultProps = {
    onTouched: null,
    onKeyDown: null,
    onBlur: null,
    touched: false,
    error: false,
    value: 'default',
    width: '100%',
    'data-cv-test-override': null,
    hideLabel: false,
    bold: false,
    canContainEmptyDefaultValue: false,
    disabled: false,
    cosigner: false,
    showSameStateText: false,
    hideErrorMessage: false,
    scrollTag: null
  };

  state = {
    isFocused: false,
    id: uuid()
  };

  onFocus = () => {
    this.setState({ isFocused: true });
  };

  onBlur = e => {
    const { onBlur } = this.props;
    if (onBlur) onBlur(e);
    this.setState({ isFocused: false });
  };

  onChange = e => {
    const { onChange } = this.props;
    onChange(e);
  };

  onKeyDown = e => {
    const { onKeyDown } = this.props;
    if (onKeyDown) {
      return onKeyDown(e);
    }

    if (e.key === 'Escape') {
      this.inputRef.current.blur();
    }

    return e;
  };

  onLabelClick = e => {
    e.preventDefault();
    this.inputRef.current.focus();
  };

  render() {
    const { isFocused, id } = this.state;
    const {
      onTouched,
      'data-cv-test-override': cvDataTestOverride,
      ...rest
    } = this.props;
    const {
      label,
      error,
      width,
      inputTip,
      disabledStateValue,
      options,
      canContainEmptyDefaultValue,
      touched,
      disabled,
      cosigner,
      showSameStateText,
      hideErrorMessage,
      scrollTag
    } = rest;

    const touchedError = touched ? error : null;

    return (
      <Fragment>
        <Container
          error={touchedError}
          width={width}
          isFocused={isFocused}
          disabled={disabled}
          cosigner={cosigner}
        >
          <Label
            onClick={this.onLabelClick}
            hasValue={isFocused || canContainEmptyDefaultValue}
            isFocused={isFocused}
            htmlFor={id}
            error={touchedError}
            id={scrollTag}
          >
            {label}
          </Label>

          <SelectInputWrapper>
            <SelectInput
              {...rest}
              id={id}
              ref={this.inputRef}
              onClick={this.onFocus}
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              onBlur={this.onBlur}
              error={touchedError}
              data-cv-test={cvDataTestOverride || rest['cv-data-test']}
            >
              <option value={disabledStateValue.value} disabled>
                {disabledStateValue.label}
              </option>
              {options.map(x => (
                <option key={x.key} value={x.key}>
                  {x.name}
                </option>
              ))}
            </SelectInput>
          </SelectInputWrapper>
        </Container>
        {touchedError && (
          <ErrorLabel error={touchedError} hideErrorMessage={hideErrorMessage}>
            {touchedError}
          </ErrorLabel>
        )}

        {inputTip && isFocused && (
          <InputTip>
            <InputTipNote>Note: </InputTipNote>
            {inputTip}
          </InputTip>
        )}
        {showSameStateText && (
          <SameStateNote>Same state is required.</SameStateNote>
        )}
      </Fragment>
    );
  }
}

export default FloatSelect;
